import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

// Bridge between React (useAuth) and the singleton API client.
// Set by <AuthTokenBridge> once Clerk is loaded so the interceptor
// can obtain tokens on every platform — including native headless
// mode where window.Clerk.session is never populated.
type TokenGetter = () => Promise<string | null>;
let _getToken: TokenGetter | null = null;

export function setAuthTokenGetter(fn: TokenGetter | null) {
  _getToken = fn;
}

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: `${API_URL}/api`,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor - add auth token
    this.client.interceptors.request.use(
      async (config) => {
        const token = await this.getSessionToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor - handle errors
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response) {
          if (error.response.status === 401) {
            // Don't redirect or sign out here. Clerk manages session lifecycle
            // independently — if the session expires, <Show when="signed-out">
            // handles navigation. Forcing signOut() on a single 401 caused
            // redirect loops when Clerk was still establishing the session.
          } else if (error.response.status === 403) {
            console.error('Forbidden access');
          } else if (error.response.status >= 500) {
            console.error('Server error:', error.response.data);
          }
        } else if (error.request) {
          console.error('Network error:', error.message);
        }
        return Promise.reject(error);
      }
    );
  }

  private async getSessionToken(): Promise<string | null> {
    try {
      // Prefer the React-bridged getter (set by <AuthTokenBridge>) —
      // it uses useAuth().getToken() which works on every platform,
      // including native headless mode where window.Clerk.session is
      // never populated.
      if (_getToken) return await _getToken();

      // Fallback: read from the global Clerk instance (web only).
      const clerk = typeof window !== 'undefined' ? (window as any).Clerk : undefined;
      if (!clerk?.session) return null;
      return await clerk.session.getToken() ?? null;
    } catch {
      return null;
    }
  }

  async get<T>(url: string, config?: AxiosRequestConfig) {
    const response = await this.client.get<T>(url, config);
    return response.data;
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig) {
    const response = await this.client.post<T>(url, data, config);
    return response.data;
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig) {
    const response = await this.client.put<T>(url, data, config);
    return response.data;
  }

  async patch<T>(url: string, data?: any, config?: AxiosRequestConfig) {
    const response = await this.client.patch<T>(url, data, config);
    return response.data;
  }

  async delete<T>(url: string, config?: AxiosRequestConfig) {
    const response = await this.client.delete<T>(url, config);
    return response.data;
  }

  async uploadFile<T>(
    url: string,
    file: File,
    additionalData?: Record<string, any>,
    onUploadProgress?: (progress: number) => void,
  ) {
    const formData = new FormData();
    formData.append('file', file);
    if (additionalData) {
      Object.keys(additionalData).forEach(key => {
        formData.append(key, additionalData[key]);
      });
    }
    const response = await this.client.post<T>(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onUploadProgress
        ? (e) => { if (e.total) onUploadProgress(Math.round((e.loaded / e.total) * 100)); }
        : undefined,
    });
    return response.data;
  }
}

export const apiClient = new ApiClient();
